import { useState } from "react";
import { QrCode, Plus, Download, Trash2, MapPin, BarChart3, DollarSign, ExternalLink, CalendarIcon } from "lucide-react";
import { format, subDays } from "date-fns";
import { ru } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  useQRCampaigns,
  useCreateQRCampaign,
  useUpdateQRCampaign,
  useDeleteQRCampaign,
  useCreateQRPoint,
  useDeleteQRPoint,
  useQRCampaignStats,
  type QRCampaign,
} from "@/hooks/useQRCampaigns";
import qrcode from "qrcode-generator";

const BASE_URL = "https://t.me/hitchef_bot";

function generateQRDataURL(url: string, type: "png" | "svg" = "png"): string {
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  if (type === "svg") {
    return qr.createSvgTag({ scalable: true });
  }
  return qr.createDataURL(8, 4);
}

function buildTrackingURL(campaign: string, content?: string | null) {
  const parts = ["qr", "offline", campaign];
  if (content) parts.push(content);
  const startParam = `utm_${parts.join("_")}`;
  return `${BASE_URL}?start=${encodeURIComponent(startParam)}`;
}

function downloadQR(url: string, filename: string, format: "png" | "svg") {
  if (format === "svg") {
    const qr = qrcode(0, "M");
    qr.addData(url);
    qr.make();
    const svg = qr.createSvgTag({ scalable: true });
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}.svg`;
    a.click();
  } else {
    const dataUrl = generateQRDataURL(url, "png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${filename}.png`;
    a.click();
  }
}

// --- Create Campaign Dialog ---
function CreateCampaignDialog({ onCreated }: { onCreated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [utmContent, setUtmContent] = useState("");
  const [cost, setCost] = useState("");
  const createMutation = useCreateQRCampaign();

  const handleCreate = () => {
    if (!name.trim()) return toast.error("Введите название");
    const utmCampaign = name.trim().replace(/\s+/g, "_");
    createMutation.mutate(
      {
        name: name.trim(),
        description: description || undefined,
        utm_campaign: utmCampaign,
        utm_content: utmContent || undefined,
        cost_of_print: cost ? parseInt(cost) : 0,
      },
      {
        onSuccess: () => {
          toast.success("Кампания создана");
          setOpen(false);
          setName("");
          setDescription("");
          setUtmContent("");
          setCost("");
          onCreated?.();
        },
        onError: (e) => toast.error(e.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="w-4 h-4 mr-1" /> Новая кампания</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Создать QR-кампанию</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Название кампании *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="RollDelivery_March" />
            {name && (
              <p className="text-xs text-muted-foreground mt-1">
                utm_campaign: <code>{name.trim().replace(/\s+/g, "_")}</code>
              </p>
            )}
          </div>
          <div>
            <Label>Описание</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Листовки в доставке роллов" />
          </div>
          <div>
            <Label>utm_content (опционально)</Label>
            <Input value={utmContent} onChange={e => setUtmContent(e.target.value)} placeholder="flyer_a5" />
          </div>
          <div>
            <Label>Стоимость печати (₽)</Label>
            <Input type="number" value={cost} onChange={e => setCost(e.target.value)} placeholder="5000" />
          </div>
          <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
            {createMutation.isPending ? "Создание..." : "Создать кампанию"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Add Point Dialog ---
function AddPointDialog({ campaignId }: { campaignId: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const createPoint = useCreateQRPoint();

  const handleAdd = () => {
    if (!name.trim()) return;
    const utmContent = name.trim().replace(/\s+/g, "_");
    createPoint.mutate(
      { campaign_id: campaignId, name: name.trim(), utm_content: utmContent },
      {
        onSuccess: () => {
          toast.success("Точка добавлена");
          setOpen(false);
          setName("");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"><MapPin className="w-3 h-3 mr-1" /> Добавить точку</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Новая точка размещения</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Название точки</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Center_Mall" />
          </div>
          <Button onClick={handleAdd} disabled={createPoint.isPending} className="w-full">Добавить</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Date period presets ---
type PeriodPreset = "7d" | "30d" | "90d" | "all" | "custom";

function PeriodSelector({ dateFrom, dateTo, onChangeFrom, onChangeTo }: {
  dateFrom: string | undefined;
  dateTo: string | undefined;
  onChangeFrom: (v: string | undefined) => void;
  onChangeTo: (v: string | undefined) => void;
}) {
  const [preset, setPreset] = useState<PeriodPreset>("all");

  const handlePreset = (p: PeriodPreset) => {
    setPreset(p);
    if (p === "all") {
      onChangeFrom(undefined);
      onChangeTo(undefined);
    } else if (p === "custom") {
      // keep current values
    } else {
      const days = p === "7d" ? 7 : p === "30d" ? 30 : 90;
      onChangeFrom(format(subDays(new Date(), days), "yyyy-MM-dd"));
      onChangeTo(format(new Date(), "yyyy-MM-dd"));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <Select value={preset} onValueChange={(v) => handlePreset(v as PeriodPreset)}>
        <SelectTrigger className="w-[160px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">За всё время</SelectItem>
          <SelectItem value="7d">Последние 7 дней</SelectItem>
          <SelectItem value="30d">Последние 30 дней</SelectItem>
          <SelectItem value="90d">Последние 90 дней</SelectItem>
          <SelectItem value="custom">Свой период</SelectItem>
        </SelectContent>
      </Select>

      {preset === "custom" && (
        <div className="flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <CalendarIcon className="w-3 h-3 mr-1" />
                {dateFrom ? format(new Date(dateFrom), "dd.MM.yy") : "От"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateFrom ? new Date(dateFrom) : undefined}
                onSelect={(d) => onChangeFrom(d ? format(d, "yyyy-MM-dd") : undefined)}
                locale={ru}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">—</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <CalendarIcon className="w-3 h-3 mr-1" />
                {dateTo ? format(new Date(dateTo), "dd.MM.yy") : "До"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateTo ? new Date(dateTo) : undefined}
                onSelect={(d) => onChangeTo(d ? format(d, "yyyy-MM-dd") : undefined)}
                locale={ru}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}

// --- Campaign Stats ---
function CampaignStatsBlock({ campaign }: { campaign: QRCampaign }) {
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const { data: stats, isLoading } = useQRCampaignStats(campaign.utm_campaign, dateFrom, dateTo);

  if (isLoading) return <Skeleton className="h-24" />;
  if (!stats) return <p className="text-sm text-muted-foreground">Нет данных</p>;

  const roi = campaign.cost_of_print > 0
    ? Math.round(((stats.revenue - campaign.cost_of_print) / campaign.cost_of_print) * 100)
    : null;
  const profit = stats.revenue - campaign.cost_of_print;

  const convColor = stats.conversion >= 30 ? "text-green-500" : stats.conversion >= 20 ? "text-yellow-500" : "text-destructive";

  return (
    <div className="space-y-4">
      <PeriodSelector dateFrom={dateFrom} dateTo={dateTo} onChangeFrom={setDateFrom} onChangeTo={setDateTo} />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { label: "Сканирования", value: stats.scans, emoji: "📱" },
          { label: "Пользователи", value: stats.users, emoji: "👥" },
          { label: "Начали создание", value: stats.startedCreation, emoji: "🎵" },
          { label: "Получили демо", value: stats.gotDemo, emoji: "🎧" },
          { label: "Оплатили", value: stats.paid, emoji: "💳" },
        ].map(s => (
          <div key={s.label} className="p-2.5 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground">{s.emoji} {s.label}</p>
            <p className="text-lg font-bold">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="p-2.5 rounded-lg bg-muted/50 text-center">
          <p className="text-[10px] text-muted-foreground">🎯 Конверсия</p>
          <p className={cn("text-lg font-bold", convColor)}>{stats.conversion}%</p>
        </div>
        <div className="p-2.5 rounded-lg bg-muted/50 text-center">
          <p className="text-[10px] text-muted-foreground">💰 Выручка</p>
          <p className="text-lg font-bold">{stats.revenue.toLocaleString()} ₽</p>
        </div>
        <div className="p-2.5 rounded-lg bg-muted/50 text-center">
          <p className="text-[10px] text-muted-foreground">📈 Ср. чек</p>
          <p className="text-lg font-bold">{stats.avgCheck} ₽</p>
        </div>
      </div>

      {campaign.cost_of_print > 0 && roi !== null && (
        <div className={cn("p-3 rounded-lg border", profit >= 0 ? "bg-green-500/5 border-green-500/20" : "bg-destructive/5 border-destructive/20")}>
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">ROI офлайн-рекламы</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground">Затраты</p>
              <p className="font-bold">{campaign.cost_of_print.toLocaleString()} ₽</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">{profit >= 0 ? "Прибыль" : "Убыток"}</p>
              <p className={cn("font-bold", profit >= 0 ? "text-green-500" : "text-destructive")}>{profit.toLocaleString()} ₽</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">ROI</p>
              <p className={cn("font-bold", roi >= 0 ? "text-green-500" : "text-destructive")}>{roi}%</p>
            </div>
          </div>
          <Badge variant={profit >= 0 ? "default" : "destructive"} className="mt-2">
            {profit >= 0 ? "✅ Окупилась" : "❌ Не окупилась"}
          </Badge>
        </div>
      )}

      {stats.byContent.length > 1 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">📍 По точкам размещения</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Точка</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Conv.</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.byContent.map(r => (
                <TableRow key={r.content}>
                  <TableCell className="font-medium text-sm">{r.content}</TableCell>
                  <TableCell className="text-right">{r.users}</TableCell>
                  <TableCell className="text-right">{r.paid}</TableCell>
                  <TableCell className="text-right">{r.conversion}%</TableCell>
                  <TableCell className="text-right">{r.revenue.toLocaleString()} ₽</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// --- Campaign Card ---
function CampaignCard({ campaign }: { campaign: QRCampaign }) {
  const [expanded, setExpanded] = useState(false);
  const [editCost, setEditCost] = useState(false);
  const [costValue, setCostValue] = useState(String(campaign.cost_of_print || 0));
  const updateCampaign = useUpdateQRCampaign();
  const deleteCampaign = useDeleteQRCampaign();
  const deletePoint = useDeleteQRPoint();

  const trackingUrl = buildTrackingURL(campaign.utm_campaign, campaign.utm_content);
  const points = campaign.points || [];

  const handleSaveCost = () => {
    updateCampaign.mutate({ id: campaign.id, cost_of_print: parseInt(costValue) || 0 });
    setEditCost(false);
    toast.success("Стоимость обновлена");
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base flex items-center gap-2">
              <QrCode className="w-4 h-4 text-primary" />
              {campaign.name}
              <Badge variant={campaign.is_active ? "default" : "secondary"} className="text-[10px]">
                {campaign.is_active ? "Активна" : "Неактивна"}
              </Badge>
            </CardTitle>
            {campaign.description && (
              <CardDescription className="mt-1">{campaign.description}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Switch
              checked={campaign.is_active}
              onCheckedChange={(v) => updateCampaign.mutate({ id: campaign.id, is_active: v })}
            />
            <Button variant="ghost" size="sm" className="text-destructive h-8 w-8 p-0"
              onClick={() => {
                if (confirm("Удалить кампанию?")) deleteCampaign.mutate(campaign.id);
              }}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* QR & URL */}
        <div className="flex gap-4 items-start">
          <div className="flex-shrink-0">
            <img src={generateQRDataURL(trackingUrl)} alt="QR" className="w-24 h-24 rounded border" />
            <div className="flex gap-1 mt-1">
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={() => downloadQR(trackingUrl, campaign.name, "png")}>
                <Download className="w-3 h-3 mr-0.5" /> PNG
              </Button>
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" onClick={() => downloadQR(trackingUrl, campaign.name, "svg")}>
                <Download className="w-3 h-3 mr-0.5" /> SVG
              </Button>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-[10px] text-muted-foreground">Tracking URL:</p>
            <code className="text-[11px] block bg-muted p-1.5 rounded break-all">{trackingUrl}</code>
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="outline" className="text-[10px]">utm_source=qr</Badge>
              <Badge variant="outline" className="text-[10px]">utm_medium=offline</Badge>
              <Badge variant="outline" className="text-[10px]">utm_campaign={campaign.utm_campaign}</Badge>
              {campaign.utm_content && <Badge variant="outline" className="text-[10px]">utm_content={campaign.utm_content}</Badge>}
            </div>
          </div>
        </div>

        {/* Cost */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">💵 Затраты на печать:</span>
          {editCost ? (
            <div className="flex items-center gap-1">
              <Input type="number" value={costValue} onChange={e => setCostValue(e.target.value)} className="h-7 w-24 text-xs" />
              <Button size="sm" className="h-7 text-xs" onClick={handleSaveCost}>Сохр.</Button>
            </div>
          ) : (
            <button onClick={() => setEditCost(true)} className="text-xs font-medium hover:underline">
              {campaign.cost_of_print > 0 ? `${campaign.cost_of_print.toLocaleString()} ₽` : "Указать"}
            </button>
          )}
        </div>

        {/* Points */}
        {points.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">📍 Точки размещения ({points.length})</p>
            <div className="flex flex-wrap gap-1">
              {points.map(p => {
                const pointUrl = buildTrackingURL(campaign.utm_campaign, p.utm_content);
                return (
                  <div key={p.id} className="flex items-center gap-1 bg-muted rounded px-2 py-1 text-xs">
                    <MapPin className="w-3 h-3" />
                    {p.name}
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => downloadQR(pointUrl, `${campaign.name}_${p.name}`, "png")}>
                      <Download className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        if (confirm(`Удалить точку ${p.name}?`)) deletePoint.mutate(p.id);
                      }}>
                      <Trash2 className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <AddPointDialog campaignId={campaign.id} />

        {/* Stats toggle */}
        <Button variant="secondary" size="sm" onClick={() => setExpanded(!expanded)}>
          <BarChart3 className="w-3.5 h-3.5 mr-1" />
          {expanded ? "Скрыть статистику" : "Показать статистику"}
        </Button>

        {expanded && <CampaignStatsBlock campaign={campaign} />}
      </CardContent>
    </Card>
  );
}

// --- Main Page ---
export default function QRCampaigns() {
  const { data: campaigns, isLoading } = useQRCampaigns();

  return (
    <AdminLayout title="📦 QR-кампании" description="Офлайн-реклама: генерация QR, отслеживание и ROI">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-muted-foreground">
          {campaigns?.length || 0} кампаний
        </p>
        <CreateCampaignDialog />
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}</div>
      ) : !campaigns?.length ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <QrCode className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Нет QR-кампаний</p>
            <p className="text-sm text-muted-foreground/60">Создайте первую кампанию для отслеживания офлайн-рекламы</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map(c => <CampaignCard key={c.id} campaign={c} />)}
        </div>
      )}
    </AdminLayout>
  );
}
